import * as React from 'react';
import classnames from 'classnames';
import {useEffect, useRef} from 'react';
import {FormattedMessage} from 'react-intl';

import {useDeviceStore, selectIsMobile} from "features/ui/store/useDeviceStore";
import {useApartmentViewerStore, selectNoViews} from "features/apartmentViewer/store/useApartmentViewerStore";
import {ThumbNailModel} from "features/apartmentViewer/ApartmentViewer/models/ThumbNailModel";
import {FurnishingModel} from "features/apartmentViewer/ApartmentViewer/models/FurnishingModel";
import {ApartmentViewerFactory} from "features/apartmentViewer/ApartmentViewer/ApartmentViewerFactory";

import 'features/apartmentViewer/components/ApartmentViewerPlayer/style.scss';

import {P} from "@atoms/Text/P";
import {Icon} from "@atoms/Media/Icon";
import {Container} from "@atoms/Layout/Container";
import {Button, ButtonColor, ButtonSize, ButtonType} from '@atoms/LinksAndButtons/Button';
import {ApartmentThumbs} from "features/apartmentViewer/components/ApartmentThumbs";
import {ApartmentCompass} from "features/apartmentViewer/components/ApartmentCompass";
import {ApartmentViewerIntro} from "features/apartmentViewer/components/ApartmentViewerIntro";
import {ApartmentViewerFacade} from "features/apartmentViewer/ApartmentViewer/ApartmentViewerFacade";
import {ApartmentFullScreenButton} from "features/apartmentViewer/components/ApartmentFullScreenButton";
import {ApartmentViewerPlayerLoader} from "features/apartmentViewer/components/ApartmentViewerPlayer/_loader";
import {
    ApartmentFurnishSelector
} from "features/apartmentViewer/components/ApartmentFurnishSelector/ApartmentFurnishSelector";

import {useQuery} from "@tanstack/react-query";
import {ApartmentViewerServiceQueryKeys} from "features/apartmentViewer/services/ApartmentViewerService/config";
import {ApartmentViewerQueryMethods} from "features/apartmentViewer/services/ApartmentViewerService/query";
import {SiteModel} from "features/apartmentViewer/ApartmentViewer/models/SiteModel";
import {Headline} from '@atoms/Text/Headline';
import {HeadlineLevel} from 'features/ui/enums';

interface Props {
    stageId: string;
    objectName: string;
}

export const ApartmentViewerPlayer : React.FC<Props> = ({stageId, objectName}) => {
    const aspectHandlerRef = React.useRef<HTMLDivElement>(null);
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const controlsRef = React.useRef<HTMLDivElement>(null);
    const [siteData, setSiteData] = React.useState<SiteModel | null>(null);

    const setNoViews = useApartmentViewerStore((state) => state.setNoViews);

    const {data, isFetched} = useQuery({
        queryKey: ApartmentViewerServiceQueryKeys.getApartmentData(stageId, objectName),
        queryFn: () => ApartmentViewerQueryMethods.getApartmentData(stageId, objectName),
    });

    useEffect(() => {
        if(isFetched && data && data.views && Object.keys(data.views).length > 0) {
            setNoViews(false);
            setSiteData(new SiteModel(data));
        } else {
            setNoViews(true);
        }
    }, [isFetched, data, setNoViews]);

    const [apartmentViewerFacade, setApartmentViewerFacade] = React.useState<ApartmentViewerFacade | null>(null);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [currentFurnishing, setCurrentFurnishing] = React.useState<string | null>(null);
    const [hoveringMarker, setHoveringMarker] = React.useState<boolean>(false);
    const [hasStarted, setHasStarted] = React.useState<boolean>(false);
    const [compassRotation, setCompassRotation] = React.useState<number>(0);
    const [showFullscreenThubms, setShowFullscreenThubms] = React.useState<boolean>(false);

    const [isFullscreen, setIsFullscreen] = React.useState<boolean>(false);
    const isFullscreenRef = useRef(isFullscreen);

    const isMobile = useDeviceStore(selectIsMobile);
    const _noViews = useApartmentViewerStore(selectNoViews);

    useEffect(() => {
        isFullscreenRef.current = isFullscreen;
    }, [isFullscreen]);

    useEffect(() => {
        if (apartmentViewerFacade) {
            window.addEventListener("resize", apartmentViewerFacade.handleWindowResize.bind(apartmentViewerFacade));
        }

        return () => {
            if (apartmentViewerFacade) {
                window.removeEventListener("resize", apartmentViewerFacade.handleWindowResize.bind(apartmentViewerFacade));
                apartmentViewerFacade.stopViewer();
            }
        };
    }, [apartmentViewerFacade]);

    useEffect(() => {
        setSizerDimensions();

        if(apartmentViewerFacade && apartmentViewerFacade.shouldUpdateMobileState(isMobile)) {
            apartmentViewerFacade.changeMobileState(isMobile);
        }

    }, [isMobile]);

    useEffect(() => {
        const handleTouchMove = (event: TouchEvent) => {
            if (canvasRef.current && canvasRef.current.contains(event.target as Node)) {
                event.preventDefault();
            }
        };

        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        window.addEventListener("touchmove", handleTouchMove, { passive: false });
        window.addEventListener("touchstart", handleTouchMove, { passive: false });
        window.addEventListener("touchend", handleTouchMove, { passive: false });
        window.addEventListener("wheel", handleTouchMove, { passive: false });
        window.addEventListener("fullscreenchange", handleFullscreenChange);

        return () => {
            window.removeEventListener("touchmove", handleTouchMove);
            window.removeEventListener("touchstart", handleTouchMove);
            window.removeEventListener("touchend", handleTouchMove);
            window.removeEventListener("wheel", handleTouchMove);
            window.removeEventListener("fullscreenchange", handleFullscreenChange);
        };
    }, []);

    const withLoading = async (asyncFunc: () => Promise<void>) => {
        setLoading(true);
        apartmentViewerFacade?.setLoadingState(true);
        try {
            await asyncFunc();
        } catch (e) {
            console.error('An error occurred:', e);
        } finally {
            setLoading(false);
            apartmentViewerFacade?.setLoadingState(false);
        }
    };

    const setSizerDimensions = () => {
        if (aspectHandlerRef.current && aspectHandlerRef.current.parentElement) {
            const aspectRatio = 16 / 9;
            let width : number, height : number;

            if (!isMobile) {
                width = aspectHandlerRef.current.parentElement.offsetWidth;
                height = width / aspectRatio;

                aspectHandlerRef.current.style.height = `${height}px`;
                aspectHandlerRef.current.style.width = '100%';//`${width}px`;
            }
            else {
                height = 0.75 * window.innerHeight;
                width = height * aspectRatio;
                aspectHandlerRef.current.style.height = `${height}px`;
                aspectHandlerRef.current.style.width = `${width}px`;
            }
        }
    };

    const startPlayer = () => withLoading(async () => {
        if (aspectHandlerRef.current && canvasRef.current && siteData) {
            setSizerDimensions();
            const apartmentViewerFacade = await ApartmentViewerFactory.createApartmentViewerFacade(canvasRef.current, siteData, isMobile);
            await apartmentViewerFacade.startViewer(onCameraRotationChange);
            apartmentViewerFacade.setActiveMarkerCallback(onHoverMarkerCallback);
            setApartmentViewerFacade(apartmentViewerFacade);
            setCurrentFurnishing(siteData.currentFurnishing);
            setHasStarted(true);
        }
    });

    const onThumbClick = (thumb: ThumbNailModel) => withLoading(async () => {
        if (apartmentViewerFacade) {
            await apartmentViewerFacade.goToCamera(thumb);
        }
    });

    const onFurnishingClick = (furnishing: FurnishingModel, callback: () => void) => withLoading(async () => {
        if (apartmentViewerFacade) {
            await apartmentViewerFacade.setNewFurnishing(furnishing);
            setCurrentFurnishing(furnishing.id);
            callback();
        }
    });

    const onFullscreenClick = async () => {
        try {
            if (controlsRef.current === null || apartmentViewerFacade === null) return;

            if (!document.fullscreenElement) {
                await controlsRef.current.requestFullscreen();
            } else {
                await document.exitFullscreen();
            }

            apartmentViewerFacade.handleWindowResize();
        } catch (e) {
            console.error('Could not enter or exit fullscreen:', e);
            throw e;
        }
    };

    const onHoverMarkerCallback = (hovering: boolean) => {
        setHoveringMarker(hovering);
    };

    const onCameraRotationChange = (rotation: number) => {
        setCompassRotation(rotation);
    };

    const getBackgroundImage = (thumb: ThumbNailModel) => {
        if(!siteData) return '';

        const domain = window.location.protocol + '//' + window.location.hostname;
        const src = `${domain}/externalfiles/image/residenceexplorer/${siteData.stageId}/360/${siteData.currentApartment?.id}/${siteData.currentFurnishing}/1600/${thumb.id}.jpg`;

        return (
            isMobile
                ? <img src={src} className="apartment-viewer-backdrop" alt={thumb.title}  />
                : <div
                    className="apartment-viewer-backdrop"
                    style={{background: `url('${src}') center center no-repeat`, backgroundSize: 'cover'}}
                />
        );
    };

    const toggleFullscreenThumbs = () => {
        setShowFullscreenThubms(!showFullscreenThubms);
    };

    if(!data || !data.views || Object.keys(data.views).length === 0) return null;

    const _className = `apartment-viewer-container ${hoveringMarker ? 'hovering' : ''}`;

    const containerClasses = classnames('apartment-viewer-container', {
        'hovering': hoveringMarker,
        'apartment-viewer--not-started': !hasStarted,
    });

    const fullscreenClasses = classnames('apartment-viewer__fullscreen-container', {
        'fullscreen': isFullscreen,
        'show-thumbnails': showFullscreenThubms,
    });

    return (
        <Container className={containerClasses} >
            <div className="apartment-viewer-header">
                <Headline level={HeadlineLevel.H2}><FormattedMessage id="jm.features.appartmentViewer.seeApartmentIn360" /></Headline>
                <P>
                    <FormattedMessage id="jm.features.appartmentViewer.disclaimerText" />
                </P>
                {(hasStarted && siteData) && <ApartmentFurnishSelector siteData={siteData} onClick={onFurnishingClick} currentFurnishing={currentFurnishing} loading={loading} />}
            </div>

            <div className="apartment-viewer-controls">
                <div ref={controlsRef} className={fullscreenClasses}>
                    <div className="wrapper" ref={wrapperRef}>
                        {(siteData && siteData.thumbNails && !hasStarted && !loading) && getBackgroundImage(siteData.thumbNails[0])}
                        {(!hasStarted && !loading && isMobile) && <ApartmentViewerIntro onStart={startPlayer} />}
                        {(hasStarted && isFullscreen && siteData) && <ApartmentFurnishSelector siteData={siteData} onClick={onFurnishingClick} currentFurnishing={currentFurnishing} loading={loading} />}

                        <div id="aspect-handler" ref={aspectHandlerRef}>
                            {!loading && hasStarted && <ApartmentCompass compassRotation={compassRotation} />}
                            {loading &&
                                <ApartmentViewerPlayerLoader />
                            }

                            {(!hasStarted && !loading && !isMobile) &&
                                <ApartmentViewerIntro onStart={startPlayer} />
                            }

                            <div id="apartment-viewer" className="apartment-viewer">
                                <canvas id={"main-canvas"} ref={canvasRef}/>
                            </div>
                        </div>

                        {(!isMobile && hasStarted) && <ApartmentFullScreenButton onClick={onFullscreenClick} />}
                        {(hasStarted && isFullscreen) &&
                            <Button
                                className="apartment-thumb-container__fold-button"
                                onClick={toggleFullscreenThumbs}
                                icon={<Icon name="chevron" up={!showFullscreenThubms} />}
                                iconBtn
                                buttonColor={ButtonColor.LIGHT}
                                buttonSize={ButtonSize.DEFAULT}
                                buttonType={ButtonType.DEFAULT}
                            >
                                <FormattedMessage id="jm.features.appartmentViewer.showThumbnails" />
                            </Button>
                        }
                    </div>

                    <div className={`apartment-thumb-container ${showFullscreenThubms ? 'open' : ''}`}>
                        {(hasStarted && siteData) && <ApartmentThumbs onClick={onThumbClick} loading={loading} siteData={siteData} />}
                    </div>
                </div>
            </div>
        </Container>
    );
};
